import { useEffect, useMemo, useState } from 'react';
import { StreamHandler } from '../apis/streaming';
import { StreamSource, StreamOptions } from './streams';

type ResourceStatus = 'pending' | 'success' | 'error';

type StreamResourceSubscriber = () => void;

const streamResourceCache = new Map<string, StreamResource<any>>();

class StreamResource<T> {
  private status: ResourceStatus = 'pending';
  private data?: T;
  private error?: any;
  private promise?: Promise<void>;
  private resolve?: () => void;
  private reject?: (reason?: any) => void;
  private subscribers = new Set<StreamResourceSubscriber>();
  private unsubscribe?: () => void;
  private source: StreamSource<T>;
  private options?: StreamOptions;
  constructor(source: StreamSource<T>, options?: StreamOptions) {
    this.source = source;
    this.options = options;
  }

  read(): T {
    if (!this.source) {
      throw new Error('StreamResource requires a source');
    }

    if (!this.unsubscribe) {
      this.attachStream();
    }
    if (this.status === 'success') {
      return this.data!;
    }

    if (this.status === 'error') {
      throw this.error;
    }

    if (!this.promise) {
      this.promise = new Promise<void>((resolve, reject) => {
        this.resolve = () => resolve();
        this.reject = (reason?: any) => reject(reason);
      });
    }

    throw this.promise;
  }

  subscribe(onUpdate: StreamResourceSubscriber): () => void {
    this.subscribers.add(onUpdate);

    if (!this.unsubscribe) {
      this.attachStream();
    }

    return () => {
      this.subscribers.delete(onUpdate);
      if (this.subscribers.size === 0) {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
      }
    };
  }

  private attachStream() {
    this.status = 'pending';
    this.unsubscribe = StreamHandler.get().subscribe(
      this.source.url,
      (rawData: unknown, _hash: string) => {
        try {
          const versionOptions = this.options?.versionOptions;
          if (versionOptions && versionOptions.xref) {
            const xref = versionOptions.xref;
            const newData = (rawData as any[]).map((current) => {
              const cached = xref.get(current[versionOptions.idAttribute]);
              if (cached && cached[versionOptions.versionAttribute] > current[versionOptions.versionAttribute]) {
                return cached;
              }
              xref.set(current[versionOptions.idAttribute], { ...current });
              return current;
            });
            this.setData(<T>newData);
          } else {
            this.setData(<T>rawData);
          }
        } catch (err) {
          this.status = 'error';
          this.error = err;
          this.reject?.(err);
          this.clearPromise();
          this.notifySubscribers();
        }
      },
    );
  }

  private setData(next: T) {
    this.data = next;
    this.status = 'success';
    this.resolve?.();
    this.clearPromise();
    this.notifySubscribers();
  }

  private clearPromise() {
    this.promise = undefined;
    this.resolve = undefined;
    this.reject = undefined;
  }

  private notifySubscribers() {
    this.subscribers.forEach((fn) => fn());
  }

  public publish(data: T) {
    this.setData(data);
  }

  public fail(error: any) {
    this.status = 'error';
    this.error = error;
    this.reject?.(error);
    this.clearPromise();
    this.notifySubscribers();
  }

  public getStatus() {
    return this.status;
  }
}

function getOrCreateResource<T>(source: StreamSource<T>, options?: StreamOptions) {
  const existing = streamResourceCache.get(source.url) as StreamResource<T> | undefined;
  if (existing) {
    return existing;
  }

  const resource = new StreamResource<T>(source, options);
  streamResourceCache.set(source.url, resource);
  return resource;
}

export function useStreamResource<T>(source?: StreamSource<T>, options?: StreamOptions) {
  const [stateTick, setStateTick] = useState(0);
  const resource = useMemo(() => {
    if (!source) {
      return undefined;
    }
    return getOrCreateResource(source, options);
  }, [source?.url, options?.versionOptions?.idAttribute, options?.versionOptions?.versionAttribute, options?.versionOptions?.xref]);

  useEffect(() => {
    if (!resource) {
      return;
    }

    const unsubscribe = resource.subscribe(() => {
      setStateTick((prev) => prev + 1);
    });

    return () => unsubscribe();
  }, [resource]);

  return resource;
}
